import { captureException } from "@sentry/nextjs";
import { FetcherResponseType } from "@website/fetcher/fetcher";
import {
  BlobFetcherType,
  FetcherType,
  RejectHandlerType
} from "@website/fetcher/fetcher/fetcher.types";
import { forceLogin, refreshToken } from "@website/fetcher/refresh-token";
import { getToken } from "@website/hooks/auth-manager";
import { getTracking } from "@website/store/tracking";
import { getChannel } from "@website/utils/channel/get-channel";
import { Channels } from "@website/utils/channel/types";
import { isSSR } from "@website/utils/is-ssr";
import { replaceOptions } from "@website/utils/replace-options";
import isArray from "lodash-es/isArray";
import isPlainObject from "lodash-es/isPlainObject";
import mapValues from "lodash-es/mapValues";
import omit from "lodash-es/omit";
import toString from "lodash-es/toString";
import values from "lodash-es/values";

const cache: Record<string, { expire: number; data: FetcherResponseType }> = {};

const rejectHandler: RejectHandlerType = ({ reject, error, extra }) => {
  reject({ error, ...extra });
  // eslint-disable-next-line no-console
  // isSSR() && console.error({ error, ...extra, date: new Date() });
  // captureException(error, {
  //   extra,
  //   tags: {
  //     errorType: "apiCallError",
  //     ApiUrl: toString(extra.url),
  //     ApiStatus: toString(extra.status)
  //   }
  // });
};

const convertObjectToErrorMessage = (data: unknown): string => {
  if (typeof data === "string") {
    return data;
  }
  if (Array.isArray(data)) {
    return data.map(convertObjectToErrorMessage).join(" & ");
  }
  if (typeof data === "object" && data !== null) {
    return Object.values(data).map(convertObjectToErrorMessage).join(" & ");
  }
  return "";
};
const buildError = (
  response: FetcherResponseType,
  configs: Record<string, unknown>,
  headers: Record<string, unknown>
) => ({
  error: `API call Error => status:${
    response.status
  } data: ${convertObjectToErrorMessage(response.data)}`,
  extra: {
    ...omit(configs, "validateStatus"),
    headers,
    response: response.data,
    status: response.status
  }
});

export const fetcher: FetcherType = (configs, openLoginParameter) => {
  const cacheKey = toString(configs?.next?.tags?.toString?.());
  const isCacheable =
    isSSR() &&
    cacheKey &&
    !["no-cache", "no-store"].includes(configs?.cache || "default");
  return new Promise((resolve, reject) => {
    if (isCacheable && cache[cacheKey]?.data) {
      if (cache[cacheKey]?.expire >= Math.floor(Date.now() / 1000)) {
        return resolve(cache[cacheKey].data);
      }
      resolve(cache[cacheKey].data);
    }
    const url = String(
      configs.options
        ? replaceOptions(configs.url, configs.options)
        : configs.url
    );
    const channel = getChannel();
    const { stToken, refreshToken: _refreshToken } = getToken();
    const accessToken = isSSR() ? "" : stToken;

    const { sid: trackingKey, experiment } = getTracking();

    const experimentKeys = values(
      mapValues(experiment, (value, key) => `${key}_${value}`)
    ).join(",");

    const isFormData = configs.data instanceof FormData;

    const headers = {
      Accept: "application/json",
      "Accept-Language": "fa",
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...((channel ? { channel } : {}) as { channel?: Channels }),
      ...(accessToken
        ? { Authorization: accessToken ? `Bearer ${accessToken}` : "" }
        : {}),
      ...(trackingKey ? { "User-Tracking-Key": toString(trackingKey) } : {}),
      ...(experimentKeys ? { "Experiment-Keys": experimentKeys } : {}),
      // ...(aid ? { Aid: aid } : {}),
      ...configs.headers
    };
    fetch(
      configs.params
        ? `${url}?${Object.entries(configs.params)
            .map(([key, value]) =>
              isArray(value)
                ? value.map((value) => `${key}=${value}`).join("&")
                : `${key}=${value}`
            )
            .join("&")}`
        : url,
      {
        cache: configs.cache ?? "default",
        method: configs.method ?? "GET",
        headers,
        body: isFormData
          ? (configs.data as BodyInit)
          : JSON.stringify(configs.data),
        ...(configs.credentials && { credentials: configs.credentials })
      }
    )
      .then((response) =>
        response
          .json()
          .then((data) => ({
            ...omit(response, ["body", "bodyUsed"]),
            data
          }))
          .catch((error) => ({
            ...omit(response, ["body", "bodyUsed"]),
            data: response.ok ? {} : error
          }))
      )
      .then((response) => {
        if (
          (response.ok || configs.validateStatus?.(response.status)) &&
          (isPlainObject(response.data?.error)
            ? Object.keys(response.data?.error).length === 0
            : !response.data?.error) &&
          ![
            toString(response.data?.success),
            toString(response.data?.successful),
            toString(response.data?.is_successfull)
          ].includes("false")
        ) {
          if (isCacheable) {
            const revalidate = configs.next?.revalidate;
            cache[cacheKey] = {
              expire:
                Math.floor(Date.now() / 1000) +
                (revalidate === false || revalidate === undefined
                  ? Number.POSITIVE_INFINITY
                  : revalidate),
              data: response
            };
          }
          return resolve(response);
        }
        if (
          response.status === 401 &&
          !isSSR() &&
          typeof window !== "undefined"
        ) {
          if (configs.__retry401) {
            captureException("multiple api call with 401", {
              tags: {
                errorType: "tokenError"
              },
              contexts: {
                request: {
                  url,
                  method: configs.method,
                  headers,
                  query: configs.params,
                  data: configs.data
                },
                response: {
                  status: response.status,
                  data: response.data
                },
                app: {
                  url: window.location.href,
                  referrer: document.referrer,
                  channel: getChannel() as Channels,
                  appName: process.env.NEXT_PUBLIC_APP_NAME,
                  appVersion: process.env.NEXT_PUBLIC_IMAGE_VERSION
                }
              }
            });
          }
          if (_refreshToken && !configs.__retry401) {
            return refreshToken()
              .then(() =>
                resolve(
                  fetcher({
                    ...configs,
                    __retry401: true
                  })
                )
              )
              .catch(() => {
                if (openLoginParameter) {
                  return forceLogin(openLoginParameter)
                    .then(() =>
                      resolve(
                        fetcher({
                          ...configs,
                          __retry401: true
                        })
                      )
                    )
                    .catch(() => {
                      rejectHandler({
                        reject,
                        ...buildError(response, configs, headers)
                      });
                    });
                }
                rejectHandler({
                  reject,
                  ...buildError(response, configs, headers)
                });
              });
          }
          if (openLoginParameter) {
            return forceLogin(openLoginParameter)
              .then(() => resolve(fetcher({ ...configs, __retry401: true })))
              .catch(() => {
                rejectHandler({
                  reject,
                  ...buildError(response, configs, headers)
                });
              });
          }
        }
        rejectHandler({
          reject,
          ...buildError(response, configs, headers)
        });
      });
  });
};

export const blobFetcher: BlobFetcherType = async (configs) => {
  const { options, url: URLConfig, method, data, params } = configs ?? {};
  const accessToken = getToken().stToken;
  const url = String(options ? replaceOptions(URLConfig, options) : URLConfig);
  const channel = getChannel();
  const trackingKey = getTracking().sid;
  const experimentKeys = values(
    mapValues(getTracking().experiment, (value, key) => `${key}_${value}`)
  ).join(",");

  const response = await fetch(
    params
      ? `${url}?${Object.entries(params)
          .map(([key, value]) =>
            isArray(value)
              ? value.map((value) => `${key}=${value}`).join("&")
              : `${key}=${value}`
          )
          .join("&")}`
      : url,
    {
      method: method || "GET",
      body: JSON.stringify(data),
      headers: {
        Accept: "application/json",
        "Accept-Language": "fa",
        ...(accessToken
          ? { Authorization: accessToken ? `Bearer ${accessToken}` : "" }
          : {}),
        ...((channel ? { channel } : {}) as { channel?: Channels }),
        ...(trackingKey ? { "User-Tracking-Key": toString(trackingKey) } : {}),
        ...(experimentKeys ? { "Experiment-Keys": experimentKeys } : {}),
        ...configs.headers
      }
    }
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response;
};
